// ui.js — UI state rendering, event binding, view updates for Peerly
// Renders from app state. Reduces scattered direct DOM mutations.

import { getState, update, subscribe, Phase, Role, generateSessionId } from './state.js';
import { FONT_SIZES, FONT_LABELS, AI } from './config.js';
import { isMobileDevice, showNotification, buildSessionUrl, el, clearChildren } from './utils.js';
import { setIcon } from './icons.js';
import { getSettings as getAISettings, saveSettings as saveAISettings, clearSettings as clearAISettings } from './ai.js';

/** Cached DOM element references */
const DOM = {};

/** Call duration timer */
let _callTimerId = null;
let _callStartTs = 0;

/**
 * Start the call duration timer.
 */
function startCallTimer() {
  stopCallTimer();
  _callStartTs = Date.now();
  _callTimerId = setInterval(() => {
    if (DOM.headerStatus) {
      const elapsed = Math.floor((Date.now() - _callStartTs) / 1000);
      const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
      const ss = String(elapsed % 60).padStart(2, '0');
      DOM.headerStatus.textContent = `In call · ${mm}:${ss}`;
    }
  }, 1000);
}

/**
 * Stop the call duration timer.
 */
function stopCallTimer() {
  if (_callTimerId) {
    clearInterval(_callTimerId);
    _callTimerId = null;
  }
}

/**
 * Initialize UI module — cache DOM refs and bind static event handlers.
 */
export function init() {
  // Cache all DOM references
  DOM.localVideo = document.getElementById('localVideo');
  DOM.remoteVideo = document.getElementById('remoteVideo');
  DOM.remoteVideoContainer = document.getElementById('remoteVideoContainer');
  DOM.localVideoContainer = document.getElementById('localVideoContainer');
  DOM.connectionPanel = document.getElementById('connectionPanel');
  DOM.chatInput = document.getElementById('chatInput');
  DOM.localNickname = document.getElementById('localNickname');
  DOM.remoteNickname = document.getElementById('remoteNickname');
  DOM.nicknameInput = document.getElementById('nicknameInput');
  DOM.customLinkInput = document.getElementById('customLinkInput');
  DOM.shareLinkText = document.getElementById('shareLinkText');
  DOM.shareLinkLabel = document.getElementById('shareLinkLabel');
  DOM.endCallBtn = document.getElementById('endCallBtn');
  DOM.shareLinkBtn = document.getElementById('shareLinkBtn');
  DOM.qrShareBtn = document.getElementById('qrShareBtn');
  DOM.emojiPicker = document.getElementById('emojiPicker');
  DOM.audioVisualizerLevel = document.getElementById('audioVisualizerLevel');
  DOM.videoSection = document.getElementById('videoSection');
  DOM.mainContainer = document.getElementById('mainContainer');
  DOM.cameraSwitch = document.getElementById('cameraSwitch');
  DOM.audioSwitch = document.getElementById('audioSwitch');
  DOM.remoteVolumeSlider = document.getElementById('remoteVolumeSlider');
  DOM.remoteVolumeControl = document.getElementById('remoteVolumeControl');
  DOM.copyLinkBtn = document.getElementById('copyLinkBtn');
  DOM.startCallBtn = document.getElementById('startCall');
  DOM.toggleVideoBtn = document.getElementById('toggleVideo');
  DOM.toggleAudioBtn = document.getElementById('toggleAudio');
  DOM.mirrorCamBtn = document.getElementById('mirrorCamBtn');
  DOM.snapshotBtn = document.getElementById('snapshotBtn');
  DOM.remoteCaptureBtn = document.getElementById('remoteCaptureBtn');
  DOM.coolModeToggle = document.getElementById('coolModeToggle');
  DOM.themeToggle = document.getElementById('themeToggle');
  DOM.fullscreenBtn = document.getElementById('fullscreenBtn');
  DOM.splitViewBtn = document.getElementById('splitViewBtn');
  DOM.swapSidesBtn = document.getElementById('swapSidesBtn');
  DOM.fontSizeBtn = document.getElementById('fontSizeBtn');
  DOM.emojiBtn = document.getElementById('emojiBtn');
  DOM.sendMessageBtn = document.getElementById('sendMessage');
  DOM.panelSubtitle = document.getElementById('panelSubtitle');
  DOM.shareLinkContainer = document.getElementById('shareLinkContainer');
  DOM.callDock = document.getElementById('callDock');
  DOM.panelQrCard = document.getElementById('panelQrCard');
  DOM.panelQrCanvas = document.getElementById('panelQrCanvas');
  DOM.qrDownloadBtn = document.getElementById('qrDownloadBtn');
  DOM.headerStatus = document.getElementById('headerStatus');
  DOM.screenShareBtn = document.getElementById('screenShareBtn');
  DOM.fileTransferBtn = document.getElementById('fileTransferBtn');
  DOM.fileInput = document.getElementById('fileInput');
  DOM.fileTransferContainer = document.getElementById('fileTransferContainer');
  DOM.pipBtn = document.getElementById('pipBtn');
  DOM.setupCardHeading = document.querySelector('.setup-card h3');
  DOM.customLinkRow = document.getElementById('customLinkRow');
  DOM.joinCodeInput = document.getElementById('joinCodeInput');
  DOM.joinCodeBtn = document.getElementById('joinCodeBtn');

  // Convert native title attributes to styled CSS tooltips
  initTooltips();

  // Restore saved preferences
  restorePreferences();
}

/**
 * JS-based floating tooltip system.
 * Creates a single div appended to document.body (escapes overflow:hidden).
 * Uses event delegation so dynamically shown buttons work without re-init.
 */
function initTooltips() {
  // Copy title → data-tooltip and suppress native tooltip
  document.querySelectorAll('[title]').forEach(el => {
    const tip = el.getAttribute('title');
    if (tip) {
      el.setAttribute('data-tooltip', tip);
      el.removeAttribute('title');
    }
  });

  // Create floating tooltip element
  const tipEl = document.createElement('div');
  tipEl.className = 'tooltip-floating';
  tipEl.id = 'tooltipFloating';
  document.body.appendChild(tipEl);

  let hideTimer = null;

  function showTooltip(target) {
    const text = target.getAttribute('data-tooltip');
    if (!text) return;
    clearTimeout(hideTimer);
    tipEl.textContent = text;
    const rect = target.getBoundingClientRect();
    tipEl.style.left = (rect.left + rect.width / 2) + 'px';
    tipEl.style.top = (rect.top - tipEl.offsetHeight - 8) + 'px';
    // If not enough space above, show below
    if (rect.top < tipEl.offsetHeight + 12) {
      tipEl.style.top = (rect.bottom + 8) + 'px';
      tipEl.classList.add('below');
    } else {
      tipEl.classList.remove('below');
    }
    tipEl.classList.add('visible');
  }

  function hideTooltip() {
    hideTimer = setTimeout(() => {
      tipEl.classList.remove('visible');
    }, 50);
  }

  // Event delegation — works for dynamically shown elements
  document.addEventListener('mouseover', (e) => {
    const target = e.target.closest('[data-tooltip]');
    if (target) showTooltip(target);
  });

  document.addEventListener('mouseout', (e) => {
    const target = e.target.closest('[data-tooltip]');
    if (target) hideTooltip();
  });

  document.addEventListener('focusin', (e) => {
    const target = e.target.closest('[data-tooltip]');
    if (target) showTooltip(target);
  });

  document.addEventListener('focusout', (e) => {
    const target = e.target.closest('[data-tooltip]');
    if (target) hideTooltip();
  });
}

/**
 * Get cached DOM references (for other modules that need specific elements).
 */
export function getDOM() {
  return DOM;
}

/**
 * Restore user preferences from localStorage.
 */
function restorePreferences() {
  // Font size
  const savedFontSize = localStorage.getItem('chatFontSize');
  if (savedFontSize !== null) {
    const idx = parseInt(savedFontSize, 10);
    if (idx >= 0 && idx < FONT_SIZES.length) {
      update({ currentFontSizeIndex: idx });
    }
  }
  applyFontSize();

  // Dark mode — restore saved preference, then fall back to system preference
  const savedDark = localStorage.getItem('darkMode');
  if (savedDark !== null) {
    const isDark = savedDark === 'true';
    update({ isDarkMode: isDark });
    document.body.classList.toggle('dark-mode', isDark);
  } else if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    update({ isDarkMode: true });
    document.body.classList.add('dark-mode');
  }

  // Cool mode
  const savedCool = localStorage.getItem('coolMode');
  if (savedCool === 'true') {
    update({ isCoolMode: true });
    document.body.classList.add('cool-mode');
  }
}

/**
 * Apply the current font size to the CSS variable and button label.
 */
function applyFontSize() {
  const state = getState();
  document.documentElement.style.setProperty('--chat-font-size', FONT_SIZES[state.currentFontSizeIndex]);
  if (DOM.fontSizeBtn) {
    DOM.fontSizeBtn.textContent = FONT_LABELS[state.currentFontSizeIndex];
  }
}

/**
 * Subscribe to state changes and update UI accordingly.
 */
export function bindStateRendering() {
  subscribe((changed, state) => {
    // Phase changes — the big one
    if ('phase' in changed) {
      renderPhase(state);
      handleDisconnectOverlay(state);
    }

    // Remote nickname
    if ('remoteNickname' in changed && DOM.remoteNickname) {
      DOM.remoteNickname.textContent = state.remoteNickname || 'Waiting for your peer…';
    }

    // Local nickname
    if ('localNickname' in changed && DOM.localNickname) {
      DOM.localNickname.textContent = state.localNickname;
    }

    // Video enabled state
    if ('isVideoEnabled' in changed) {
      if (DOM.toggleVideoBtn) {
        setIcon(DOM.toggleVideoBtn, state.isVideoEnabled ? 'camera' : 'cameraOff');
        DOM.toggleVideoBtn.setAttribute('aria-pressed', String(state.isVideoEnabled));
      }
      DOM.localVideoContainer?.classList.toggle('video-off', !state.isVideoEnabled);
    }

    // Audio enabled state
    if ('isAudioEnabled' in changed && DOM.toggleAudioBtn) {
      setIcon(DOM.toggleAudioBtn, state.isAudioEnabled ? 'mic' : 'micOff');
      DOM.toggleAudioBtn.setAttribute('aria-pressed', String(state.isAudioEnabled));
      DOM.toggleAudioBtn.classList.toggle('btn-muted', !state.isAudioEnabled);
    }

    // Mirror state
    if ('isMirrored' in changed && DOM.localVideo) {
      DOM.localVideo.style.transform = state.isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
    }

    // Cool mode
    if ('isCoolMode' in changed) {
      document.querySelectorAll('.video-container').forEach(c =>
        c.classList.toggle('cool-mode', state.isCoolMode)
      );
      document.body.classList.toggle('cool-mode', state.isCoolMode);
      localStorage.setItem('coolMode', String(state.isCoolMode));
    }

    // Dark mode
    if ('isDarkMode' in changed) {
      document.body.classList.toggle('dark-mode', state.isDarkMode);
      localStorage.setItem('darkMode', String(state.isDarkMode));
    }

    // Fullscreen
    if ('isFullscreen' in changed && DOM.remoteVideoContainer) {
      DOM.remoteVideoContainer.classList.toggle('fullscreen-mode', state.isFullscreen);
    }

    // Split view
    if ('isSplitView' in changed) {
      renderSplitView(state);
    }

    // Swapped
    if ('isSwapped' in changed && DOM.videoSection) {
      DOM.videoSection.classList.toggle('swapped', state.isSwapped);
    }

    // Emoji picker
    if ('isEmojiPickerOpen' in changed && DOM.emojiPicker) {
      DOM.emojiPicker.classList.toggle('active', state.isEmojiPickerOpen);
      DOM.emojiBtn?.setAttribute('aria-expanded', String(state.isEmojiPickerOpen));
    }

    // Font size
    if ('currentFontSizeIndex' in changed) {
      applyFontSize();
    }

    // Remote stream
    if ('remoteStream' in changed && DOM.remoteVideo) {
      DOM.remoteVideo.srcObject = state.remoteStream;
      if (state.remoteStream) {
        DOM.remoteVideoContainer?.classList.remove('loading');
      }
    }

    // Local stream
    if ('localStream' in changed && DOM.localVideo) {
      DOM.localVideo.srcObject = state.localStream;
    }

    // Screen sharing state
    if ('isScreenSharing' in changed && DOM.screenShareBtn) {
      DOM.screenShareBtn.classList.toggle('active-share', state.isScreenSharing);
      DOM.screenShareBtn.setAttribute('aria-pressed', String(state.isScreenSharing));
      setIcon(DOM.screenShareBtn, state.isScreenSharing ? 'screenShareOff' : 'screenShare');
    }

    // PiP state
    if ('isPiPActive' in changed && DOM.pipBtn) {
      DOM.pipBtn.setAttribute('aria-pressed', String(state.isPiPActive));
      setIcon(DOM.pipBtn, state.isPiPActive ? 'pipOff' : 'pip');
    }
  });
}

/**
 * Render UI for the current phase.
 */
function renderPhase(state) {
  const { phase } = state;
  const mobile = isMobileDevice();

  switch (phase) {
    case Phase.IDLE:
      stopCallTimer();
      DOM.connectionPanel?.classList.remove('hidden');
      DOM.connectionPanel?.classList.remove('exiting');
      DOM.callDock?.classList.add('hidden');
      DOM.endCallBtn?.classList.add('hidden');
      DOM.shareLinkBtn?.classList.add('hidden');
      DOM.qrShareBtn?.classList.add('hidden');
      DOM.fullscreenBtn?.classList.add('hidden');
      DOM.splitViewBtn?.classList.add('hidden');
      DOM.swapSidesBtn?.classList.add('hidden');
      DOM.remoteCaptureBtn?.classList.add('hidden');
      DOM.remoteVolumeControl?.classList.add('hidden');
      DOM.screenShareBtn?.classList.add('hidden');
      DOM.fileTransferBtn?.classList.add('hidden');
      DOM.pipBtn?.classList.add('hidden');
      DOM.remoteVideoContainer?.classList.remove('loading', 'fullscreen-mode');
      DOM.videoSection?.classList.remove('split-view', 'swapped');
      DOM.mainContainer?.classList.remove('split-active');
      if (DOM.remoteNickname) DOM.remoteNickname.textContent = 'Waiting for your peer…';
      if (DOM.localVideo) DOM.localVideo.srcObject = null;
      if (DOM.remoteVideo) DOM.remoteVideo.srcObject = null;
      if (DOM.headerStatus) DOM.headerStatus.textContent = '';
      break;

    case Phase.PREPARING_MEDIA:
      if (DOM.connectionPanel && !DOM.connectionPanel.classList.contains('hidden')) {
        DOM.connectionPanel.classList.add('exiting');
        setTimeout(() => {
          DOM.connectionPanel.classList.add('hidden');
          DOM.connectionPanel.classList.remove('exiting');
        }, 400);
      }
      DOM.callDock?.classList.remove('hidden');
      DOM.shareLinkBtn?.classList.remove('hidden');
      DOM.qrShareBtn?.classList.remove('hidden');
      DOM.remoteVideoContainer?.classList.add('loading');
      if (DOM.headerStatus) DOM.headerStatus.textContent = 'Preparing camera & mic…';
      break;

    case Phase.CREATING_ROOM:
    case Phase.WAITING_FOR_PEER:
      DOM.remoteVideoContainer?.classList.add('loading');
      if (DOM.headerStatus) DOM.headerStatus.textContent = 'Waiting for your peer…';
      break;

    case Phase.JOINING_ROOM:
    case Phase.CONNECTING:
      DOM.remoteVideoContainer?.classList.add('loading');
      if (DOM.headerStatus) DOM.headerStatus.textContent = 'Connecting…';
      break;

    case Phase.CONNECTED:
      DOM.remoteVideoContainer?.classList.remove('loading');
      DOM.localVideoContainer?.classList.remove('loading');
      DOM.shareLinkBtn?.classList.remove('hidden');
      DOM.qrShareBtn?.classList.remove('hidden');
      DOM.fullscreenBtn?.classList.remove('hidden');
      DOM.remoteCaptureBtn?.classList.remove('hidden');
      DOM.remoteVolumeControl?.classList.remove('hidden');
      DOM.screenShareBtn?.classList.remove('hidden');
      DOM.fileTransferBtn?.classList.remove('hidden');
      DOM.pipBtn?.classList.remove('hidden');
      if (!mobile) {
        DOM.splitViewBtn?.classList.remove('hidden');
      }
      if (DOM.headerStatus) DOM.headerStatus.textContent = '';
      startCallTimer();
      break;

    case Phase.RECONNECTING:
      stopCallTimer();
      if (DOM.headerStatus) DOM.headerStatus.textContent = 'Reconnecting…';
      break;

    case Phase.ENDED:
    case Phase.FAILED:
      stopCallTimer();
      // endCall handler resets to IDLE after teardown
      break;
  }
}

/**
 * Render split view state.
 */
function renderSplitView(state) {
  if (!DOM.videoSection || !DOM.mainContainer) return;

  DOM.videoSection.classList.toggle('split-view', state.isSplitView);
  DOM.mainContainer.classList.toggle('split-active', state.isSplitView);

  if (state.isSplitView) {
    DOM.swapSidesBtn?.classList.remove('hidden');
    DOM.fullscreenBtn?.classList.add('hidden');
    DOM.remoteVideoContainer?.classList.remove('fullscreen-mode');
  } else {
    DOM.swapSidesBtn?.classList.add('hidden');
    if (state.phase === Phase.CONNECTED) {
      DOM.fullscreenBtn?.classList.remove('hidden');
    }
    DOM.videoSection?.classList.remove('swapped');
    update({ isSwapped: false });
  }
}

/**
 * Manage disconnect overlay based on phase.
 */
function handleDisconnectOverlay(state) {
  if (state.phase === Phase.RECONNECTING && !state.disconnectOverlay) {
    const overlay = el('div', { className: 'disconnect-overlay' });
    const msg = el('div', { className: 'disconnect-message' });
    msg.appendChild(document.createTextNode('Connection lost'));
    const sub = el('span', { textContent: 'Trying to reconnect…' });
    sub.style.display = 'block';
    sub.style.fontSize = '1rem';
    sub.style.marginTop = '0.5rem';
    sub.style.opacity = '0.9';
    msg.appendChild(sub);
    overlay.appendChild(msg);
    DOM.remoteVideoContainer?.appendChild(overlay);
    update({ disconnectOverlay: overlay });
  } else if (state.phase === Phase.CONNECTED && state.disconnectOverlay) {
    state.disconnectOverlay.remove();
    update({ disconnectOverlay: null });
  } else if ((state.phase === Phase.IDLE || state.phase === Phase.ENDED) && state.disconnectOverlay) {
    state.disconnectOverlay.remove();
    update({ disconnectOverlay: null });
  }
}

/**
 * Populate device select dropdowns.
 */
export function populateDeviceSelects(videoDevices, audioDevices) {
  if (DOM.cameraSwitch) {
    clearChildren(DOM.cameraSwitch);
    if (videoDevices.length === 0) {
      DOM.cameraSwitch.appendChild(el('option', { value: '', disabled: true, textContent: 'No camera found' }));
    }
    videoDevices.forEach((device, i) => {
      const option = el('option', {
        value: device.deviceId,
        textContent: device.label || `Camera ${i + 1}`,
      });
      DOM.cameraSwitch.appendChild(option);
    });
    if (videoDevices.length > 0) DOM.cameraSwitch.selectedIndex = 0;
  }
  if (DOM.audioSwitch) {
    clearChildren(DOM.audioSwitch);
    if (audioDevices.length === 0) {
      DOM.audioSwitch.appendChild(el('option', { value: '', disabled: true, textContent: 'No microphone found' }));
    }
    audioDevices.forEach((device, i) => {
      const option = el('option', {
        value: device.deviceId,
        textContent: device.label || `Microphone ${i + 1}`,
      });
      DOM.audioSwitch.appendChild(option);
    });
    if (audioDevices.length > 0) DOM.audioSwitch.selectedIndex = 0;
  }
}

/**
 * Set up the connection panel for sender vs receiver roles.
 */
export function configureConnectionPanel(role, sessionUrl) {
  const devicePreview = document.getElementById('devicePreview');
  if (role === 'receiver') {
    if (DOM.setupCardHeading) DOM.setupCardHeading.textContent = 'Ready to Join';
    if (DOM.panelSubtitle) DOM.panelSubtitle.textContent = "You've been invited to a call";
    if (DOM.customLinkRow) DOM.customLinkRow.style.display = 'none';
    if (DOM.customLinkInput) DOM.customLinkInput.style.display = 'none';
    if (DOM.shareLinkContainer) DOM.shareLinkContainer.style.display = 'none';
    if (DOM.copyLinkBtn) DOM.copyLinkBtn.style.display = 'none';
    if (DOM.panelQrCard) DOM.panelQrCard.classList.add('hidden');
    if (DOM.qrDownloadBtn) DOM.qrDownloadBtn.style.display = 'none';
    if (devicePreview) devicePreview.style.display = 'none';
    if (DOM.startCallBtn) DOM.startCallBtn.textContent = 'Join Call';
    DOM.nicknameInput?.focus();
  } else {
    if (DOM.setupCardHeading) DOM.setupCardHeading.textContent = 'Set up your call';
    if (DOM.startCallBtn) DOM.startCallBtn.textContent = 'Create Room';
    // Fully restore sender-mode controls (undo any receiver-mode hiding)
    if (DOM.panelSubtitle) DOM.panelSubtitle.textContent = 'Check your camera and mic before joining.';
    if (DOM.customLinkRow) DOM.customLinkRow.style.display = '';
    if (DOM.customLinkInput) DOM.customLinkInput.style.display = '';
    if (DOM.shareLinkContainer) DOM.shareLinkContainer.style.display = '';
    if (DOM.copyLinkBtn) DOM.copyLinkBtn.style.display = '';
    if (DOM.panelQrCard) DOM.panelQrCard.classList.remove('hidden');
    if (DOM.qrDownloadBtn) DOM.qrDownloadBtn.style.display = '';
    if (devicePreview) devicePreview.style.display = '';
    if (DOM.shareLinkText) DOM.shareLinkText.textContent = sessionUrl;
    renderPanelQR(sessionUrl);
    setTimeout(() => {
      showNotification('Press Shift+? for keyboard shortcuts', 'info');
    }, 2000);
  }
}

/**
 * Update the share link display when custom input changes.
 */
export function updateShareLinkDisplay(customSlug, defaultId) {
  const id = customSlug || defaultId;
  const url = buildSessionUrl(id);
  if (DOM.shareLinkText) DOM.shareLinkText.textContent = url;
  if (DOM.shareLinkLabel) {
    DOM.shareLinkLabel.textContent = customSlug ? 'Your custom link' : 'Your link';
  }
  renderPanelQR(url);
}

/**
 * Generate a QR code canvas for the given URL.
 * Returns the canvas element with the QR code drawn on it.
 */
function generateQRCanvas(url) {
  const qr = qrcode(4, 'L');
  qr.addData(url);
  qr.make();

  const qrCells = qr.getModuleCount();
  const cellSize = 6;
  const margin = 12;
  const canvasSize = qrCells * cellSize + margin * 2;

  const canvas = document.createElement('canvas');
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasSize, canvasSize);
  ctx.fillStyle = '#000000';

  for (let row = 0; row < qrCells; row++) {
    for (let col = 0; col < qrCells; col++) {
      if (qr.isDark(row, col)) {
        ctx.fillRect(col * cellSize + margin, row * cellSize + margin, cellSize, cellSize);
      }
    }
  }

  return canvas;
}

/**
 * Render the QR code into the panel canvas element.
 */
function renderPanelQR(url) {
  if (!DOM.panelQrCanvas) return;
  const canvas = generateQRCanvas(url);
  const ctx = DOM.panelQrCanvas.getContext('2d');
  DOM.panelQrCanvas.width = canvas.width;
  DOM.panelQrCanvas.height = canvas.height;
  ctx.drawImage(canvas, 0, 0);

  // Enable buttons now that QR is available
  if (DOM.copyLinkBtn) DOM.copyLinkBtn.disabled = false;
  if (DOM.qrDownloadBtn) DOM.qrDownloadBtn.disabled = false;

  // Hide aurora placeholder
  const placeholder = DOM.panelQrCard?.querySelector('.qr-placeholder');
  if (placeholder) placeholder.classList.add('qr-placeholder--hidden');
}

/**
 * Attach focus trap, Escape handler, and focus restoration to a modal overlay.
 * @param {HTMLElement} overlay - the modal overlay element
 * @param {{ onEscape?: () => void }} [opts] - optional Escape handler (defaults to overlay.remove)
 */
function attachModalA11y(overlay, { onEscape } = {}) {
  const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const focusTrap = (e) => {
    if (e.key === 'Tab') {
      const focusable = overlay.querySelectorAll(focusableSelector);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const escHandler = (e) => {
    if (e.key === 'Escape') {
      if (onEscape) onEscape();
      else overlay.remove();
    }
  };

  document.addEventListener('keydown', escHandler);
  document.addEventListener('keydown', focusTrap);

  const triggerBtn = document.activeElement;
  const origRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    origRemove();
    document.removeEventListener('keydown', escHandler);
    document.removeEventListener('keydown', focusTrap);
    if (triggerBtn && typeof triggerBtn.focus === 'function') triggerBtn.focus();
  };
}

/**
 * Show the QR code modal using safe DOM APIs.
 */
export function showQRModal() {
  const state = getState();
  const url = state.roomId ? buildSessionUrl(state.roomId) : window.location.href;

  const canvas = generateQRCanvas(url);

  // Build modal using safe DOM
  const overlay = el('div', {
    className: 'modal-overlay',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'QR Code Share',
  });

  const container = el('div', { className: 'qr-code-container' });
  container.appendChild(el('h3', { textContent: 'Invite with QR' }));

  const displayCanvas = document.createElement('canvas');
  displayCanvas.width = canvas.width;
  displayCanvas.height = canvas.height;
  displayCanvas.getContext('2d').drawImage(canvas, 0, 0);
  displayCanvas.style.display = 'block';
  displayCanvas.style.margin = '1rem auto';
  container.appendChild(displayCanvas);

  container.appendChild(el('p', { textContent: 'Scan to join or share the link' }));

  const btnRow = el('div', { className: 'qr-buttons' });

  const copyBtn = el('button', {
    className: 'btn btn-primary',
    textContent: 'Copy Image',
    'aria-label': 'Copy QR code to clipboard',
    events: {
      click: async () => {
        try {
          const blob = await new Promise(resolve => canvas.toBlob(resolve));
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          showNotification('QR code copied to clipboard!', 'success');
        } catch {
          showNotification('Failed to copy QR code', 'error');
        }
      },
    },
  });

  const downloadBtn = el('button', {
    className: 'btn btn-success',
    textContent: 'Download Image',
    'aria-label': 'Download QR code image',
    events: {
      click: () => {
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `peerly-qr-${Date.now()}.png`;
        a.click();
        showNotification('QR code downloaded!', 'success');
      },
    },
  });

  const closeBtn = el('button', {
    className: 'btn btn-secondary',
    textContent: 'Close',
    'aria-label': 'Close QR modal',
    events: {
      click: () => overlay.remove(),
    },
  });

  btnRow.appendChild(copyBtn);
  btnRow.appendChild(downloadBtn);
  btnRow.appendChild(closeBtn);
  container.appendChild(btnRow);
  overlay.appendChild(container);

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  attachModalA11y(overlay);

  document.body.appendChild(overlay);

  // Focus the close button for accessibility
  closeBtn.focus();
}

/**
 * Show a confirmation modal and return a Promise<boolean>.
 * Resolves true if confirmed, false if cancelled (button, Escape, or backdrop).
 *
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.message
 * @param {string} [opts.confirmLabel='Confirm']
 * @param {string} [opts.cancelLabel='Cancel']
 * @param {string} [opts.confirmClass='btn-danger']
 * @returns {Promise<boolean>}
 */
export function showConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmClass = 'btn-danger',
}) {
  return new Promise((resolve) => {
    let resolved = false;
    const finish = (value) => {
      if (resolved) return;
      resolved = true;
      overlay.remove();
      resolve(value);
    };

    const overlay = el('div', {
      className: 'modal-overlay',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': title,
    });

    const container = el('div', { className: 'confirm-modal-container' });
    container.appendChild(el('h3', { textContent: title }));
    container.appendChild(el('p', { textContent: message }));

    const btnRow = el('div', { className: 'confirm-modal-buttons' });

    const cancelBtn = el('button', {
      className: 'btn btn-secondary',
      textContent: cancelLabel,
      'aria-label': cancelLabel,
      events: { click: () => finish(false) },
    });

    const confirmBtn = el('button', {
      className: `btn ${confirmClass}`,
      textContent: confirmLabel,
      'aria-label': confirmLabel,
      events: { click: () => finish(true) },
    });

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(confirmBtn);
    container.appendChild(btnRow);
    overlay.appendChild(container);

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) finish(false);
    });

    attachModalA11y(overlay, { onEscape: () => finish(false) });

    document.body.appendChild(overlay);
    confirmBtn.focus();
  });
}

/**
 * Show a transient "leaving call" overlay (best-effort for beforeunload).
 * If the page unloads, the overlay is irrelevant. If the user stays, it is
 * removed after a tick.
 */
export function showLeaveCallOverlay() {
  const overlay = el('div', {
    className: 'modal-overlay leave-call-overlay',
    role: 'alertdialog',
    'aria-modal': 'true',
    'aria-label': 'Leaving call',
  });

  const container = el('div', { className: 'confirm-modal-container' });
  container.appendChild(el('h3', { textContent: 'Leave call?' }));
  container.appendChild(
    el('p', {
      textContent: 'You are about to leave an active call. Are you sure?',
    }),
  );

  overlay.appendChild(container);
  document.body.appendChild(overlay);

  // If this fires, the user stayed — remove the overlay
  setTimeout(() => {
    overlay.remove();
  }, 0);

  return overlay;
}

/**
 * Get the local and remote video elements.
 */
export function getVideoElements() {
  return { localVideo: DOM.localVideo, remoteVideo: DOM.remoteVideo };
}

/**
 * Render or remove the connection quality badge on the remote video.
 * @param {'good'|'weak'|'poor'|null} quality
 */
export function renderQualityBadge(quality) {
  if (!DOM.remoteVideoContainer) return;

  let badge = DOM.remoteVideoContainer.querySelector('.quality-badge');

  if (!quality) {
    if (badge) badge.remove();
    return;
  }

  const config = {
    good: { label: '● Good', className: 'quality-good' },
    weak: { label: '● Fair', className: 'quality-weak' },
    poor: { label: '● Poor', className: 'quality-poor' },
  };
  const cfg = config[quality];
  if (!cfg) return;

  if (!badge) {
    badge = el('div', { className: `quality-badge ${cfg.className}` });
    DOM.remoteVideoContainer.appendChild(badge);
  } else {
    badge.className = `quality-badge ${cfg.className}`;
  }
  badge.textContent = cfg.label;
}

/**
 * Render a visual error state on the connection panel.
 * @param {'roomFull'|'roomNotFound'|'roomEnded'|'default'} mode
 * @param {string} message — human-readable error message
 */
export function renderPanelError(mode, message) {
  if (!DOM.connectionPanel) return;

  clearPanelError();

  if (mode === 'default' || !mode) {
    showNotification(message, 'error');
    return;
  }

  const icons = {
    roomFull: 'lock',
    roomNotFound: 'search',
    roomEnded: 'phoneHangup',
  };

  const actions = {
    roomFull: { label: 'Start New Call', action: 'start-new' },
    roomNotFound: { label: 'Try Another Link', action: 'try-another' },
    roomEnded: { label: 'Start New Call', action: 'start-new' },
  };

  const errorBox = el('div', { className: `panel-error panel-error-${mode}` });

  const iconEl = el('div', { className: 'panel-error-icon' });
  setIcon(iconEl, icons[mode] || 'warning');
  errorBox.appendChild(iconEl);
  errorBox.appendChild(el('p', { className: 'panel-error-msg', textContent: message }));

  const actCfg = actions[mode];
  if (actCfg) {
    const btn = el('button', {
      className: 'btn btn-primary panel-error-btn',
      textContent: actCfg.label,
      events: {
        click: () => {
          clearPanelError();
          if (actCfg.action === 'start-new') {
            const newId = generateSessionId();
            update({ roomId: newId, role: Role.SENDER, panelMode: 'default' });
            const url = buildSessionUrl(newId);
            configureConnectionPanel('sender', url);
            window.history.replaceState({}, '', window.location.pathname);
          } else if (actCfg.action === 'try-another') {
            update({ panelMode: 'default' });
            if (DOM.customLinkInput) {
              DOM.customLinkInput.value = '';
              DOM.customLinkInput.style.display = '';
              DOM.customLinkInput.focus();
            }
            if (DOM.panelSubtitle) {
              DOM.panelSubtitle.textContent = 'Enter a room name or use the auto-generated link';
            }
          }
        },
      },
    });
    errorBox.appendChild(btn);
  }

  // Insert after the subtitle
  if (DOM.panelSubtitle && DOM.panelSubtitle.parentNode) {
    DOM.panelSubtitle.parentNode.insertBefore(errorBox, DOM.panelSubtitle.nextSibling);
  } else {
    DOM.connectionPanel.appendChild(errorBox);
  }
}

/**
 * Clear any panel error state.
 */
export function clearPanelError() {
  if (!DOM.connectionPanel) return;
  const existing = DOM.connectionPanel.querySelector('.panel-error');
  if (existing) existing.remove();
}

/**
 * Render or update a file transfer progress item.
 * @param {{ id: string, name: string, size: number, direction: string, progress: number, status: string }} transfer
 */
export function renderFileProgress(transfer) {
  if (!DOM.fileTransferContainer) return;

  let item = DOM.fileTransferContainer.querySelector(`[data-ft-id="${transfer.id}"]`);

  if (transfer.status === 'done') {
    if (item) {
      item.classList.add('ft-done');
      const fill = item.querySelector('.ft-bar-fill');
      if (fill) fill.style.width = '100%';
      const pct = item.querySelector('.ft-percent');
      if (pct) pct.textContent = '100%';
      setTimeout(() => item.remove(), 2500);
    }
    return;
  }

  if (transfer.status === 'rejected') {
    if (item) {
      item.classList.add('ft-rejected');
      const pct = item.querySelector('.ft-percent');
      if (pct) pct.textContent = 'Rejected';
      setTimeout(() => item.remove(), 2500);
    }
    return;
  }

  if (!item) {
    item = el('div', { className: 'file-transfer-item' });
    item.setAttribute('data-ft-id', transfer.id);
    const icon = el('span', { className: 'ft-icon' });
    setIcon(icon, transfer.direction === 'out' ? 'upload' : 'download');
    const info = el('div', { className: 'ft-info' });
    info.appendChild(el('div', { className: 'ft-name', textContent: transfer.name }));
    const bar = el('div', { className: 'ft-bar' });
    bar.appendChild(el('div', { className: 'ft-bar-fill', style: 'width: 0%' }));
    info.appendChild(bar);
    item.appendChild(icon);
    item.appendChild(info);
    item.appendChild(el('span', { className: 'ft-percent', textContent: '0%' }));
    DOM.fileTransferContainer.appendChild(item);
  }

  if (transfer.status === 'pending') {
    const pct = item.querySelector('.ft-percent');
    if (pct) pct.textContent = 'Waiting…';
    return;
  }

  const fill = item.querySelector('.ft-bar-fill');
  if (fill) fill.style.width = `${transfer.progress}%`;
  const pct = item.querySelector('.ft-percent');
  if (pct) pct.textContent = `${transfer.progress}%`;
}

/**
 * Clear all file transfer progress items.
 */
export function clearFileTransfers() {
  if (DOM.fileTransferContainer) {
    clearChildren(DOM.fileTransferContainer);
  }
}

/**
 * Show a file transfer request prompt with accept/deny buttons.
 * @param {{ id: string, name: string, size: number }} fileInfo
 * @param {() => void} onAccept
 * @param {() => void} onDeny
 */
export function renderFileRequest(fileInfo, onAccept, onDeny) {
  if (!DOM.fileTransferContainer) return;

  const item = el('div', { className: 'file-transfer-item ft-request' });
  item.setAttribute('data-ft-id', fileInfo.id);

  const icon = el('span', { className: 'ft-icon' });
  setIcon(icon, 'download');

  const info = el('div', { className: 'ft-info' });
  info.appendChild(el('div', { className: 'ft-name', textContent: fileInfo.name }));
  info.appendChild(
    el('div', {
      className: 'ft-size',
      textContent: _formatFileSize(fileInfo.size),
    }),
  );

  const actions = el('div', { className: 'ft-actions' });
  const acceptBtn = el('button', {
    className: 'btn btn-success ft-accept-btn',
    textContent: 'Accept',
    events: {
      click: () => {
        onAccept();
        item.remove();
      },
    },
  });
  const denyBtn = el('button', {
    className: 'btn btn-secondary ft-deny-btn',
    textContent: 'Deny',
    events: {
      click: () => {
        onDeny();
        item.remove();
      },
    },
  });
  actions.appendChild(acceptBtn);
  actions.appendChild(denyBtn);

  item.appendChild(icon);
  item.appendChild(info);
  item.appendChild(actions);
  DOM.fileTransferContainer.appendChild(item);
}

/**
 * Format a file size in bytes to a human-readable string.
 */
function _formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Show the AI settings modal — configure endpoint, API key, and model.
 * Settings are persisted to localStorage.
 */
export function showAISettingsModal() {
  const current = getAISettings();

  const overlay = el('div', {
    className: 'modal-overlay',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'AI Settings',
  });

  const container = el('div', { className: 'ai-settings-modal' });

  container.appendChild(el('h3', { textContent: 'AI Assistant Settings' }));
  container.appendChild(
    el('p', {
      className: 'ai-settings-desc',
      textContent: 'Configure any OpenAI-compatible API (OpenRouter, OpenAI, local LLMs). Your key is stored locally in your browser only.',
    }),
  );

  // Endpoint field
  const endpointLabel = el('label', {
    className: 'ai-settings-label',
    textContent: 'API Endpoint',
    htmlFor: 'aiEndpointInput',
  });
  const endpointInput = el('input', {
    type: 'url',
    id: 'aiEndpointInput',
    className: 'ai-settings-input',
    value: current.endpoint,
    placeholder: AI.DEFAULT_ENDPOINT,
    autocomplete: 'off',
  });
  container.appendChild(endpointLabel);
  container.appendChild(endpointInput);

  // API Key field
  const keyLabel = el('label', {
    className: 'ai-settings-label',
    textContent: 'API Key',
    htmlFor: 'aiKeyInput',
  });
  const keyInput = el('input', {
    type: 'password',
    id: 'aiKeyInput',
    className: 'ai-settings-input',
    value: current.apiKey,
    placeholder: 'sk-or-v1-…',
    autocomplete: 'off',
  });
  container.appendChild(keyLabel);
  container.appendChild(keyInput);

  // Model field
  const modelLabel = el('label', {
    className: 'ai-settings-label',
    textContent: 'Model',
    htmlFor: 'aiModelInput',
  });
  const modelInput = el('input', {
    type: 'text',
    id: 'aiModelInput',
    className: 'ai-settings-input',
    value: current.model,
    placeholder: AI.DEFAULT_MODEL,
    autocomplete: 'off',
  });
  container.appendChild(modelLabel);
  container.appendChild(modelInput);

  // Button row
  const btnRow = el('div', { className: 'ai-settings-buttons' });

  const clearBtn = el('button', {
    className: 'btn btn-secondary',
    textContent: 'Clear',
    'aria-label': 'Clear saved AI settings',
    events: {
      click: () => {
        clearAISettings();
        endpointInput.value = AI.DEFAULT_ENDPOINT;
        keyInput.value = '';
        modelInput.value = AI.DEFAULT_MODEL;
        showNotification('AI settings cleared', 'success');
      },
    },
  });

  const cancelBtn = el('button', {
    className: 'btn btn-secondary',
    textContent: 'Cancel',
    'aria-label': 'Cancel',
    events: { click: () => overlay.remove() },
  });

  const saveBtn = el('button', {
    className: 'btn btn-success',
    textContent: 'Save',
    'aria-label': 'Save AI settings',
    events: {
      click: () => {
        const endpoint = endpointInput.value.trim() || AI.DEFAULT_ENDPOINT;
        const apiKey = keyInput.value.trim();
        const model = modelInput.value.trim() || AI.DEFAULT_MODEL;
        saveAISettings({ endpoint, apiKey, model });
        showNotification('AI settings saved', 'success');
        overlay.remove();
      },
    },
  });

  btnRow.appendChild(clearBtn);
  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(saveBtn);
  container.appendChild(btnRow);

  overlay.appendChild(container);

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  attachModalA11y(overlay);

  document.body.appendChild(overlay);
  saveBtn.focus();
}
